import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, MessageCirclePlus } from 'lucide-react'
import { userClient } from '../../lib/userClient'
import { WaybookPost } from '../../types'
import { useUserAuthStore } from '../../store/userAuthStore'

export default function UserPostsPage() {
  const { user } = useUserAuthStore()
  const [posts, setPosts] = useState<WaybookPost[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [showComposer, setShowComposer] = useState(false)
  const [privacy, setPrivacy] = useState<'public' | 'friends' | 'private'>('public')
  const [contentType, setContentType] = useState<'text' | 'trip' | 'event' | 'photo' | 'gallery'>('text')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const fetchPosts = useCallback(async () => {
    if (!user?.id) return

    setLoading(true)
    setError(null)
    try {
      const response = await userClient.getPosts({ user_id: user.id, limit: 30 })
      const payload = (response.data?.data || []) as WaybookPost[]
      setPosts(Array.isArray(payload) ? payload : [])
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to load posts'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  useEffect(() => {
    fetchPosts()
  }, [fetchPosts])

  const orderedPosts = useMemo(
    () => posts.slice().sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
    [posts]
  )

  const handleCreatePost = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!description.trim()) {
      setSubmitError('Post text is required')
      return
    }

    setSubmitting(true)
    setSubmitError(null)
    try {
      await userClient.createPost({
        content_type: contentType,
        description: description.trim(),
        privacy,
      })
      setDescription('')
      setShowComposer(false)
      fetchPosts()
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to publish post'
      setSubmitError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-8">
      <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <MessageCirclePlus className="h-5 w-5" />
              Share something new
            </h2>
            <p className="text-sm text-gray-600">
              Posts are created via <code>POST /posts</code> and will appear in your public feed depending on privacy.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowComposer((value) => !value)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
          >
            {showComposer ? 'Close composer' : 'Create post'}
          </button>
        </div>

        {showComposer && (
          <form onSubmit={handleCreatePost} className="mt-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="post-type">
                  Type
                </label>
                <select
                  id="post-type"
                  className="input w-full"
                  value={contentType}
                  onChange={(event) =>
                    setContentType(event.target.value as typeof contentType)
                  }
                >
                  <option value="text">Text</option>
                  <option value="trip">Trip highlight</option>
                  <option value="event">Event update</option>
                  <option value="photo">Photo</option>
                  <option value="gallery">Gallery</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="post-privacy">
                  Privacy
                </label>
                <select
                  id="post-privacy"
                  className="input w-full"
                  value={privacy}
                  onChange={(event) =>
                    setPrivacy(event.target.value as typeof privacy)
                  }
                >
                  <option value="public">Public</option>
                  <option value="friends">Friends only</option>
                  <option value="private">Private</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="post-text" className="block text-sm font-medium text-gray-700 mb-1">
                What's on your mind?
              </label>
              <textarea
                id="post-text"
                className="input w-full min-h-[140px]"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Share an update with your followers..."
              />
            </div>

            {submitError && (
              <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-700">
                {submitError}
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary inline-flex items-center gap-2"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Publish post
              </button>
            </div>
          </form>
        )}
      </section>

      <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Your posts</h2>
            <p className="text-sm text-gray-600">Fetched via <code>GET /posts</code> filtered by your user id.</p>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
            </div>
          ) : error ? (
            <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          ) : orderedPosts.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-200 px-6 py-10 text-center text-sm text-gray-500">
              You haven't published any posts yet.
            </div>
          ) : (
            orderedPosts.map((post) => (
              <article
                key={post.id}
                className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm"
              >
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="uppercase font-semibold tracking-wide">{post.content_type}</span>
                  <span>{new Date(post.created_at).toLocaleString()}</span>
                </div>
                {post.description && (
                  <p className="mt-3 text-sm text-gray-700 whitespace-pre-wrap">{post.description}</p>
                )}
                <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-500">
                  <span>Privacy: {post.privacy}</span>
                  {post.stats && (
                    <>
                      {'likes' in post.stats && (
                        <span>Likes: {post.stats.likes ?? 0}</span>
                      )}
                      {'comments' in post.stats && (
                        <span>Comments: {post.stats.comments ?? 0}</span>
                      )}
                    </>
                  )}
                </div>
              </article>
            ))
          )}
        </div>
      </section>
    </div>
  )
}
